// The languages Pinecast renders in, and how to name them — without asking
// the server.
//
// The list is static: it comes from `i18n.json`, the same file the build reads
// to decide which locale bundles to compile, so a picker built from it can
// never disagree with what was actually shipped. The names come from
// `Intl.DisplayNames`, which is the platform's own copy of the CLDR data the
// backend reaches for Babel to get. Between them there is nothing left for a
// round trip to fetch.
import config from '../../../i18n.json';

/**
 * Every supported language, in Django's lower-cased spelling.
 *
 * Lower-cased because that is the form everything else on the page speaks:
 * `<html lang>`, `Content-Language`, and the `language` column the mutation
 * writes. `i18n.json` spells the targets `pt-BR`, which only the bundle
 * directory names care about (see `pinecast.i18n.bundle_dir`).
 */
export const SUPPORTED_LOCALES: ReadonlyArray<string> = [
  config.locale.source,
  ...config.locale.targets,
].map(code => code.toLowerCase());

export type LanguageOption = {
  code: string;
  /** The language's name, in `displayLocale`. */
  name: string;
  /** The language's name in itself — "dansk", not "Danish". */
  nameLocal: string;
};

const displayName = (code: string, inLocale: string): string => {
  try {
    return (
      new Intl.DisplayNames([inLocale], {type: 'language'}).of(code) ?? code
    );
  } catch {
    // A locale `Intl` will not parse, which should be impossible for a code
    // out of `i18n.json` — but a picker showing a raw tag still works, and a
    // picker that threw would not.
    return code;
  }
};

/**
 * Every supported language, named for a picker.
 *
 * Both names, because a picker needs both: `name` labels the option in the
 * language the reader is currently getting, and `nameLocal` is how somebody
 * who has landed in a language they cannot read finds their own again.
 */
export const languageOptions = (
  displayLocale: string,
): ReadonlyArray<LanguageOption> =>
  SUPPORTED_LOCALES.map(code => ({
    code,
    name: displayName(code, displayLocale),
    nameLocal: displayName(code, code),
  }));

/**
 * The language the page around us is being served in.
 *
 * The server renders `<html lang>` from the same resolution that decides
 * everything else on the page (`pinecast.i18n.language_for_request`), so this
 * is the current setting as far as the reader is concerned — no query needed,
 * and it stays right on the tip jar, where the answer depends on a session
 * this bundle knows nothing about.
 */
export const documentLocale = (): string => {
  if (typeof document === 'undefined') {
    return SUPPORTED_LOCALES[0];
  }
  const lang = document.documentElement.lang.toLowerCase();
  return SUPPORTED_LOCALES.includes(lang) ? lang : SUPPORTED_LOCALES[0];
};
